import React, { useEffect, useMemo, useState } from "react";

import AddCircleRoundedIcon from "@mui/icons-material/AddCircleRounded";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import ClearIcon from "@mui/icons-material/ClearOutlined";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import SearchIcon from "@mui/icons-material/Search";
import Accordion from "@mui/material/Accordion";
import AccordionDetails from "@mui/material/AccordionDetails";
import AccordionSummary from "@mui/material/AccordionSummary";
import Backdrop from "@mui/material/Backdrop";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import CardActionArea from "@mui/material/CardActionArea";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import CircularProgress from "@mui/material/CircularProgress";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import Skeleton from "@mui/material/Skeleton";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { List } from "linqts";
import { IoFlagOutline } from "react-icons/io5";
import { NumericFormat } from "react-number-format";
import { useQuery } from "react-query";
import { makeStyles } from "tss-react/mui";

import ChildBookingReason from "./ChildBookingReason";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { getCreateCasualBookingsPreview } from "../../store/modules/booking/bookingActions";
import { downloadFileById, getNationalProgramDays } from "../../store/modules/digitalProgram/digitalProgramActions";
import {
  serviceRocketeersProgramBeginCheckout,
  serviceRocketeersProgramSelect,
  serviceRocketeersProgramsView,
  serviceRocketeersProgramView,
  viewRocketeersProgramByClassification,
} from "../../store/modules/program/programStateActions";
import { getUnAvailableSessionDays } from "../../store/modules/session/sessionActions";
import theme from "../../theme";
import { BookingDateInfo, BookingProgram, ChildAgeConstraint, RollDetails, SelectedChild } from "../../types/models";
import {
  ClassificationEnum,
  IBookingProgram,
  IGetCreateCasualBookingsPreviewRequest,
  INationalProgramDayInfo,
  ISelectedChild,
  ISessionAvailability,
  RocketeersBookingInfo,
} from "../../types/types";
import {
  getAvailabilityReasonsText,
  getChildDisplayName,
  getStyleColor,
  isContactAllowedToBook,
} from "../../utils/helpers";
import { ButtonPrimary } from "../Common/Buttons/Buttons";
import TabPanel from "../Common/Tab/TabPanel";

interface INationalProgramSelectionProps {
  formState: RocketeersBookingInfo;
  onAddBooking: (program: IBookingProgram) => void;
  onRemoveBooking: (program: IBookingProgram) => void;
  constraints: ChildAgeConstraint[];
  onChildReasonChanged(childId: number, reason: string): void;
  onContinueClicked: () => void;
  pageState: {
    hasConflict: boolean;
    hasError: boolean;
    unAvailableDates: ISessionAvailability[];
    newlyAddedUnAvailableDates: ISessionAvailability[];
  };
  setPageState: React.Dispatch<
    React.SetStateAction<{
      hasConflict: boolean;
      hasError: boolean;
      unAvailableDates: ISessionAvailability[];
      newlyAddedUnAvailableDates: ISessionAvailability[];
    }>
  >;
}
function a11yProps(index: number) {
  return {
    id: `event-tab-${index}`,
    "aria-controls": `event-tabpanel-${index}`,
  };
}
const useVacStyles = makeStyles()((theme) => {
  return {
    button: {
      pointerEvents: "auto",
    },
    selected: {
      border: "2px solid var(--custom-color-rocketeer)",
      backgroundColor: "#daf3ff",
    },
    selectedVac: {
      border: "2px solid var(--custom-color-rocketeer)",
      backgroundColor: "#daf3ff",
    },

    accordion: {
      backgroundColor: getStyleColor("--custom-color-light-grey"),
      border: "1px solid var(--youroshc-color-primary)",
      borderRadius: "10px",
      marginBottom: theme.spacing(1),
      "&::before": {
        display: "none",
      },
    },

    accordionSummary: {
      alignItems: "flex-start",
      borderRadius: "10px",
      backgroundColor: "white",
    },
    backdrop: {
      zIndex: theme.zIndex.drawer + 1,
      color: "white",
    },
  };
});

function NationalProgramSelection(props: INationalProgramSelectionProps) {
  const {
    formState,
    onAddBooking,
    onRemoveBooking,
    constraints,
    onChildReasonChanged,
    onContinueClicked,
    pageState,
    setPageState,
  } = props;
  const { classes } = useVacStyles();
  const auth = useAppSelector((state) => state.auth);
  const profile = auth.user?.profile;
  const customerAccount = useAppSelector((state) => state.customer.customer);
  const unAvailableDates = useAppSelector((state) => state.unAvailableDates.dates);

  const [selectedProgram, setSelectedProgram] = useState<{
    program: INationalProgramDayInfo | undefined;
    ageIsGreater: boolean;
    ageIsLess: boolean;
  }>({
    program: undefined,
    ageIsGreater: false,
    ageIsLess: false,
  });
  const commonData = useAppSelector((state) => state.commonData.data);
  const isAllowedToBook = isContactAllowedToBook(
    customerAccount!,
    auth.userId!,
    parseInt(profile!.customer_contact_id!.toString())
  );
  const [tabIndex, setTabIndex] = React.useState(0);

  const dispatch = useAppDispatch();
  const [programDays, setProgramDays] = useState<INationalProgramDayInfo[]>([]);
  const [filteredServicePrograms, setFilteredServicePrograms] = useState<INationalProgramDayInfo[]>(programDays ?? []);
  const [searchEvents, setSearchEvents] = useState("");

  var availableClassifications =
    commonData?.classifications?.filter((x) => programDays.findIndex((y) => y.classificationId === x.id) >= 0) ?? [];

  useEffect(() => {
    if (selectedProgram.program) {
      dispatch(serviceRocketeersProgramSelect(selectedProgram.program, "select_item"));
      dispatch(serviceRocketeersProgramView(selectedProgram.program));
    }
  }, [selectedProgram]);
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabIndex(newValue);

    dispatch(viewRocketeersProgramByClassification(programDays.filter((x) => x.classificationId === newValue)));
  };

  const programDaysResult = useQuery(
    ["getNationalProgramDays", formState.service?.serviceId],
    () => getNationalProgramDays(profile!.customer_account_id!, formState.service!.serviceId),
    {
      refetchOnWindowFocus: false,
      enabled: profile?.customer_account_id !== undefined && formState.service !== undefined,
    }
  );

  var isEventsLoading = programDaysResult.isLoading;

  useEffect(() => {
    if (programDaysResult.isSuccess && programDaysResult.data) {
      setProgramDays(programDaysResult.data);
      dispatch(serviceRocketeersProgramsView(programDaysResult.data));
    }
  }, [programDaysResult.status]);

  function getFilteredServicePrograms() {
    var classification = commonData?.classifications?.find((x) => x.id === tabIndex);
    if (classification) {
      return programDays.filter((x) => x.classificationId === classification?.id) ?? [];
    }
    return programDays;
  }

  useEffect(() => {
    setFilteredServicePrograms(programDays);
    setTabIndex(0);
  }, [programDays]);

  useEffect(() => {
    const events = getFilteredServicePrograms();
    setFilteredServicePrograms(
      events.filter((x) => searchEvents.trim() === "" || x.theme!.toLowerCase().includes(searchEvents.toLowerCase()))
    );
  }, [searchEvents, tabIndex]);

  function getConstraintsByProgram(program: INationalProgramDayInfo) {
    return [
      new ChildAgeConstraint({
        rollDate: program.date,
        session: new RollDetails(program.session),
        isExcursion: program.classificationId === ClassificationEnum.Excursion,
      }),
    ];
  }

  function isFriendlyFee() {
    return customerAccount?.isFriendlyFeeEligible === true;
  }

  function isChildAgeGreaterThanMax(constraints: ChildAgeConstraint[], child: SelectedChild) {
    return constraints.filter((ct) => ct !== undefined && ct.isAgeGreaterThanMax(child));
  }

  function hasMaxAgeViolation(): boolean {
    if (constraints.length === 0 || formState.children.length === 0) return false;
    return formState.children.some(
      (c) =>
        isChildAgeGreaterThanMax(constraints, new SelectedChild(c)).length > 0 &&
        (c.reason === undefined || c.reason === null || c.reason.trim() === "")
    );
  }

  useEffect(() => {
    loadUnAvailableDates();
  }, [formState.children.length]);

  function getMatchingUnAvailableDates(unAvailableDates: ISessionAvailability[] | null) {
    if (!unAvailableDates) {
      return new Array<ISessionAvailability>();
    }
    const casualDates = new List(programDays.map((d) => d) ?? []);
    return new List(unAvailableDates)
      .Where(
        (ud) =>
          ud !== undefined &&
          !ud.isAvailableForCasualBooking &&
          casualDates.Any((cd) => cd !== undefined && cd.rollId === ud.rollId && cd.date.isSame(ud.date, "date"))
      )
      .ToArray();
  }

  function loadUnAvailableDates() {
    if (profile?.customer_account_id) {
      if (formState.children.length === 0) return;
      var from = dayjs();
      var to = from.add(4, "month");
      getUnAvailableSessionDays(
        dispatch,
        profile!.customer_account_id as number,
        from as dayjs.Dayjs,
        to as dayjs.Dayjs,
        programDays.map((x) => x.rollId),
        formState.children.map((c) => c.child.childId as number)
      );
    }
  }

  useEffect(() => {
    if (unAvailableDates) {
      var unvDates = getMatchingUnAvailableDates(unAvailableDates);
      setPageState({
        ...pageState,
        unAvailableDates: unvDates,
      });
      var avDates = formState.dates
        .map((x) => new BookingDateInfo(x.item.rollId, x.item.date))
        .filter((x) => unvDates.findIndex((y) => y.rollId === x.rollId && y.date.isSame(x.date, "date")) < 0);
      if (avDates.length > 0) {
        getCreateCasualBookingsPreview(
          dispatch,
          profile!.customer_account_id! as number,
          {
            childrenIds: formState.children.map((c) => c.child.childId as number),
            dates: avDates,
          } as IGetCreateCasualBookingsPreviewRequest
        );
      } else {
        var newUnAvailableDates = unvDates.filter(
          (x) => formState.dates.findIndex((y) => y.item.rollId === x.rollId && y.item.date.isSame(x.date, "date")) >= 0
        );
        setPageState({
          hasConflict: true,
          unAvailableDates: unvDates,
          newlyAddedUnAvailableDates: newUnAvailableDates,
          hasError: false,
        });
      }
    }
  }, [unAvailableDates]);

  useEffect(() => {
    setFilteredServicePrograms(new Array<INationalProgramDayInfo>());
    setTabIndex(0);
  }, [formState.service]);

  useEffect(() => {
    loadUnAvailableDates();
  }, [formState.dates]);

  return (
    <Grid container direction={"column"}>
      <Backdrop className={classes.backdrop} open={isEventsLoading}>
        <CircularProgress color="primary" />
      </Backdrop>
      {!selectedProgram.program && (
        <Grid item xs={12}>
          <TextField
            fullWidth
            sx={{
              "& fieldset": {
                borderRadius: "10px",
                backgroundColor: "#c7c5c5",
                opacity: 0.2,
              },
            }}
            value={searchEvents}
            placeholder="Learning event"
            variant="outlined"
            onChange={async (event: React.ChangeEvent<HTMLInputElement>) => {
              setSearchEvents(event.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),

              endAdornment:
                searchEvents.length > 0 ? (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setSearchEvents("")}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null,
            }}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        {!selectedProgram.program && (
          <Box sx={{ borderBottom: 1, borderColor: "divider", maxWidth: { xs: 250, sm: "100%" } }}>
            <Tabs
              value={tabIndex}
              onChange={handleTabChange}
              aria-label="Event Type"
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
            >
              <Tab label="All" {...a11yProps(0)} />
              {availableClassifications.map((at) => (
                <Tab key={`AvitityTypeTab_${at.id}`} label={at.name} {...a11yProps(at.id)} value={at.id} />
              ))}
            </Tabs>
          </Box>
        )}
        <TabPanel value={tabIndex} index={tabIndex}>
          <Grid container spacing={2} padding={2}>
            {!selectedProgram.program &&
              filteredServicePrograms?.map(function (p, index) {
                var constraints = getConstraintsByProgram(p);
                const ageIsLess = isAgeLessThanMin(new List(constraints));

                function isAgeGreaterThanMax(constraints: ChildAgeConstraint[]) {
                  return constraints.filter(
                    (ct) =>
                      ct !== undefined &&
                      formState.children.some((child: ISelectedChild) =>
                        ct.isAgeGreaterThanMax(new SelectedChild(child))
                      )
                  );
                }
                const ageIsGreater = isAgeGreaterThanMax(constraints).length > 0;
                return (
                  <Grid item xs={12} sm={6} md={3} lg={3} key={`Program_${p.programVersionId}_${p.sessionId}`}>
                    <ServiceEventSelection
                      isAnyChildAgeGreaterThanMax={ageIsGreater}
                      isAnyChildAgeLessThanMin={ageIsLess}
                      unAvailableDates={pageState.unAvailableDates ?? []}
                      isFriendlyFee={isFriendlyFee()}
                      program={p}
                      selected={formState.dates.some(
                        (x) => x.item.bookingProgramId === new BookingProgram(p).bookingProgramId
                      )}
                      selectedClassName={classes.selectedVac}
                      onSelect={() => {
                        setSelectedProgram({
                          program: p,
                          ageIsGreater: ageIsGreater,
                          ageIsLess: ageIsLess,
                        });
                      }}
                    />
                  </Grid>
                );
              })}

            {!selectedProgram.program && filteredServicePrograms?.length === 0 && (
              <Grid item xs={12}>
                <Typography>No events found.</Typography>
              </Grid>
            )}
            {selectedProgram.program && (
              <Grid item xs={12}>
                <ServiceEventDetail
                  isAnyChildAgeGreaterThanMax={selectedProgram.ageIsGreater}
                  isAnyChildAgeLessThanMin={selectedProgram.ageIsLess}
                  isFriendlyFee={isFriendlyFee()}
                  isAllowedToBook={isAllowedToBook}
                  program={selectedProgram.program}
                  unAvailableDates={pageState.unAvailableDates ?? []}
                  onDismiss={() => setSelectedProgram({ program: undefined, ageIsGreater: false, ageIsLess: false })}
                  isBooked={formState.dates.some(
                    (x) => x.item.bookingProgramId === new BookingProgram(selectedProgram.program!).bookingProgramId
                  )}
                  onBook={() => {
                    onAddBooking(new BookingProgram(selectedProgram.program!));
                    dispatch(serviceRocketeersProgramSelect(selectedProgram.program!, "add_to_cart"));
                    dispatch(serviceRocketeersProgramSelect(selectedProgram.program!, "begin_checkout"));

                    setSelectedProgram({ program: undefined, ageIsGreater: false, ageIsLess: false });
                  }}
                  onRemove={() => {
                    dispatch(serviceRocketeersProgramSelect(selectedProgram.program!, "remove_from_cart"));
                    onRemoveBooking(new BookingProgram(selectedProgram.program!));
                    setSelectedProgram({ program: undefined, ageIsGreater: false, ageIsLess: false });
                  }}
                />
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </Grid>

      {!selectedProgram.program && formState.children.some((x) => x.reasonRequired) && (
        <Grid item xs={12}>
          <Grid container direction={"row"} spacing={1} p={2} rowGap={1}>
            <Grid item xs={12} sm={12}>
              <Typography variant="h6" gutterBottom>
                <strong> Need more information</strong>
              </Typography>
            </Grid>

            {formState.children
              .filter((x) => x.reasonRequired)
              .map((child, index) => {
                const ageIsGreater = isChildAgeGreaterThanMax(constraints, new SelectedChild(child));
                const chidlName = getChildDisplayName(child.child);

                return (
                  <Grid item xs={12} sm={12} key={index}>
                    <ChildBookingReason
                      chidlName={chidlName}
                      initialReason={child.reason ?? ""}
                      rollName={ageIsGreater[0]?.session.rollName ?? ""}
                      selected={classes.selected}
                      onChange={(reason) => {
                        onChildReasonChanged(child.child.childId!, reason);
                      }}
                    />
                  </Grid>
                );
              })}
          </Grid>
        </Grid>
      )}
      {!selectedProgram.program && (
        <Grid item xs={12}>
          <ButtonPrimary
            disabled={formState.dates.length === 0 || hasMaxAgeViolation()}
            className={classes.button}
            onClick={() => {
              if (formState.dates.length === 0) return;
              onContinueClicked();
              dispatch(serviceRocketeersProgramBeginCheckout(formState.dates.map((f) => f.item)));
            }}
            sx={{
              backgroundColor: getStyleColor("--custom-color-rocketeer"),
              color: "black",
              float: "right",
              width: "200px",
              fontWeight: "bold",
            }}
          >
            Continue
          </ButtonPrimary>
        </Grid>
      )}
    </Grid>
  );

  function isAgeLessThanMin(constraints: List<ChildAgeConstraint>) {
    return constraints.Any(
      (ct) => ct !== undefined && formState.children.some((child) => ct.isAgeLessThanMin(new SelectedChild(child)))
    );
  }
}

interface IServiceProgramSelectionProps {
  program: INationalProgramDayInfo;
  isFriendlyFee: boolean;
  onSelect: () => void;
  unAvailableDates: ISessionAvailability[];
  selected: boolean;
  selectedClassName: string;
  isAnyChildAgeLessThanMin: boolean;
  isAnyChildAgeGreaterThanMax: boolean;
}
function ServiceEventSelection(props: IServiceProgramSelectionProps): JSX.Element {
  const {
    program,
    isFriendlyFee,
    onSelect,
    unAvailableDates,
    selected,
    selectedClassName,
    isAnyChildAgeGreaterThanMax,
    isAnyChildAgeLessThanMin,
  } = props;

  const [imageUrl, setImageUrl] = useState<{ isDownloading: boolean; url: string }>({
    isDownloading: false,
    url: "",
  });

  const unvDate = useMemo(
    () => unAvailableDates.find((d) => d.date.isSame(program.date, "date") && !d.isAvailableForCasualBooking),
    [unAvailableDates]
  );

  useEffect(() => {
    if (imageUrl.url === null || imageUrl.url.length === 0) {
      setImageUrl({ ...imageUrl, isDownloading: true });
      downloadFileById(program.previewFileId)
        .then((data) => {
          if (data) {
            setImageUrl({
              isDownloading: false,
              url: URL.createObjectURL(data.data),
            });
          } else {
            setImageUrl({
              isDownloading: false,
              url: "",
            });
          }
        })
        .catch((error) => {
          setImageUrl({
            isDownloading: false,
            url: "",
          });
        });
    }
  }, [program.previewFileId]);
  return (
    <Card sx={{ height: "100%", border: "1px solid #707070" }} className={selected ? selectedClassName : ""}>
      <CardActionArea sx={{ height: "100%" }} onClick={onSelect}>
        <Box sx={{ position: "relative" }}>
          {!imageUrl.isDownloading && (
            <CardMedia
              component={"img"}
              sx={{ width: "100%", objectFit: "cover" }}
              src={imageUrl.url}
              title="event image"
            ></CardMedia>
          )}
          {imageUrl.isDownloading && <Skeleton variant="rectangular" width="100%" height={200} />}
          {(unvDate || isAnyChildAgeGreaterThanMax || isAnyChildAgeLessThanMin) && (
            <Box
              sx={{
                position: "absolute",
                bottom: 0,
                opacity: 1,
                right: 0,
                bgcolor: "var(--custom-color-rocketeer)",
                padding: "5px",
                borderTopRightRadius: "10px",
              }}
            >
              <Typography color={"black"} fontSize={"12px"}>
                {unvDate && getAvailabilityReasonsText(unvDate.reasonForCasualBooking)}
                {!unvDate && isAnyChildAgeLessThanMin && "Age is less than min"}

                {!unvDate && !isAnyChildAgeLessThanMin && isAnyChildAgeGreaterThanMax && "Age is greater than max"}
              </Typography>
            </Box>
          )}
        </Box>
        <CardContent sx={{ height: "100%" }}>
          <Grid container direction="column" padding={1}>
            <Typography variant="h6" fontWeight={"bold"} fontSize={"15px"}>
              {program.theme}
            </Typography>
            <Typography variant="subtitle2" className="threeLines" gutterBottom fontSize={"14px"}>
              {program.shortBlurb}
            </Typography>
            <Typography pt={1} variant="subtitle2" fontSize={"12px"}>
              {program.date.format("DD MMM YYYY")}
            </Typography>

            <Typography fontSize={"20px"}>
              <strong>
                <NumericFormat
                  displayType="text"
                  fixedDecimalScale={true}
                  value={
                    isFriendlyFee || !program.isCasualFee
                      ? program.session.primaryFeeAmount
                      : program.session.casualFeeAmount
                  }
                  decimalScale={2}
                  prefix="$"
                  suffix="*"
                />
              </strong>
            </Typography>

            <Typography fontSize={"10px"} variant="caption">
              *Prices exclude available Child Care Subsidy (CCS)
            </Typography>
          </Grid>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

interface IServiceEventDetailProps {
  program: INationalProgramDayInfo;
  isFriendlyFee: boolean;
  unAvailableDates: ISessionAvailability[];
  onDismiss?: () => void;
  onBook: () => void;
  onRemove: () => void;
  isAllowedToBook: boolean;
  isBooked: boolean;
  isAnyChildAgeLessThanMin: boolean;
  isAnyChildAgeGreaterThanMax: boolean;
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexWrap: "wrap",
  },
  listItem: {
    backgroundColor: "white",
    borderRadius: "10px",
    marginBottom: theme.spacing(1),
  },
  button: {
    color: "black",
    textDecoration: "underline",
    fontWeight: "bold",
  },
  pill: {
    maxWidth: 60,
    "@media screen and (min-width: 1280px)": {
      maxWidth: 200,
    },
  },
  image: {
    width: 60,
    height: 45,
    overflow: "hidden",
    backgroundRepeat: "no-repeat",
    backgroundPosition: "center center",
    backgroundSize: "contain",
    display: "block",
    marginBottom: theme.spacing(1),
    borderRadius: "10px",

    "@media screen and (min-width: 1280px)": {
      width: 200,
      height: 150,
    },
  },
  accordion: {
    backgroundColor: "white",

    borderRadius: "10px",
    marginBottom: theme.spacing(1),

    border: "1px solid var(--custom-color-rocketeer)",
    borderLeftWidth: 5,
  },
  accordionSummary: {
    alignItems: "flex-start",
    borderRadius: "10px",
    backgroundColor: getStyleColor("--custom-color-light-grey"),
  },
}));
function ServiceEventDetail(props: IServiceEventDetailProps) {
  const {
    program,
    isFriendlyFee,
    unAvailableDates,
    onDismiss,
    onBook,
    isAllowedToBook,
    isBooked,
    onRemove,
    isAnyChildAgeGreaterThanMax,
    isAnyChildAgeLessThanMin,
  } = props;
  const { classes } = useStyles();

  const [imageUrl, setImageUrl] = useState<{ isDownloading: boolean; url: string }>({
    isDownloading: false,
    url: "",
  });

  const unvDate = useMemo(
    () => unAvailableDates.find((d) => d.date.isSame(program.date, "date") && !d.isAvailableForCasualBooking),
    [unAvailableDates]
  );

  useEffect(() => {
    if (imageUrl.url === "") {
      setImageUrl({ ...imageUrl, isDownloading: true });
      downloadFileById(program.previewFileId)
        .then((data) => {
          if (data) {
            setImageUrl({
              isDownloading: false,
              url: URL.createObjectURL(data.data),
            });
          } else {
            setImageUrl({
              isDownloading: false,
              url: "",
            });
          }
        })
        .catch((error) => {
          setImageUrl({
            isDownloading: false,
            url: "",
          });
        });
    }
  }, [program.previewFileId]);
  var lo = program.learningOutcomes[0];
  return (
    <ListItem alignItems="flex-start" className={classes.listItem}>
      <ListItemAvatar style={{ paddingRight: theme.spacing(2) }}>
        <div
          aria-label={program.theme}
          role="img"
          className={classes.image}
          style={
            imageUrl.url.length > 0
              ? { backgroundImage: `url(${imageUrl.url})` }
              : { backgroundColor: getStyleColor("--custom-color-dark-grey") }
          }
        ></div>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Grid container spacing={1}>
            <Grid item xs>
              <Typography fontSize={"16px"} fontWeight="bold">
                {program.theme}
              </Typography>

              <Typography variant="subtitle2" gutterBottom fontSize={"14px"}>
                {program.shortBlurb}
              </Typography>
              <Grid container direction={"row"} rowGap={1} pt={1}>
                <Grid item xs={12}>
                  <Stack direction={"row"} spacing={2}>
                    <CalendarTodayIcon fontSize="small" />
                    <Typography variant="subtitle2" fontSize={"16px"}>
                      {program?.date?.format("MMMM DD, YYYY")} &nbsp;| &nbsp;
                      {dayjs(program.session.startTime).format("hh:mm a") +
                        " to " +
                        dayjs(program.session.endTime).format("hh:mm a")}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12}>
                  <Stack direction={"row"} spacing={2}>
                    <LocationOnOutlinedIcon fontSize="small" />
                    <Typography variant="subtitle2" fontSize={"16px"}>
                      {program.serviceName}
                    </Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} pb={1}>
                  <Stack direction={"row"} spacing={2}>
                    <AttachMoneyIcon fontSize="small" />
                    <Grid container direction="column">
                      <Typography variant="subtitle2" fontSize={"16px"} fontWeight="bold">
                        <NumericFormat
                          displayType="text"
                          value={
                            isFriendlyFee || !program.isCasualFee
                              ? program.session.primaryFeeAmount
                              : program.session.casualFeeAmount
                          }
                          decimalScale={2}
                          fixedDecimalScale={true}
                          prefix="$"
                          suffix="*"
                        />
                      </Typography>
                      <Typography fontSize={"10px"} variant="caption">
                        *Prices exclude available Child Care Subsidy (CCS)
                      </Typography>
                    </Grid>
                  </Stack>
                </Grid>
              </Grid>
              {program?.longBlurb && (
                <Accordion className={classes.accordion} elevation={1}>
                  <AccordionSummary
                    className={classes.accordionSummary}
                    expandIcon={
                      <AddCircleRoundedIcon
                        sx={{ pt: "10px" }}
                        alignmentBaseline="central"
                        htmlColor="var(--custom-color-rocketeer)"
                      />
                    }
                  >
                    <Typography variant="body1" fontWeight={"bold"}>
                      Highlights
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ bgcolor: "background.paper" }}>
                    <Grid container rowSpacing={2}>
                      <Grid item xs={12}>
                        <div
                          className="dangerouslySetInnerHTML"
                          dangerouslySetInnerHTML={{
                            __html: program.longBlurb,
                          }}
                        ></div>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontSize={"16px"} fontWeight="bold">
                          {program.experienceName}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Typography fontSize={"16px"}>
                          <strong>LO</strong> {lo.groupOrder}.{lo.order} {lo.name}
                        </Typography>
                      </Grid>
                    </Grid>
                  </AccordionDetails>
                </Accordion>
              )}

              <Box marginTop={1} />

              {unvDate && (
                <Grid container alignItems="flex-start">
                  <Grid item>
                    <IoFlagOutline color={theme.palette.error.main} style={{ marginTop: 4, marginRight: 10 }} />
                  </Grid>
                  <Grid item xs>
                    <span style={{ color: theme.palette.error.main }}>
                      {getAvailabilityReasonsText(unvDate.reasonForCasualBooking)}
                    </span>
                  </Grid>
                </Grid>
              )}

              {!unvDate && isAnyChildAgeLessThanMin && (
                <Grid container alignItems="flex-start">
                  <Grid item>
                    <IoFlagOutline color={theme.palette.error.main} style={{ marginTop: 4, marginRight: 10 }} />
                  </Grid>
                  <Grid item xs>
                    <span style={{ color: theme.palette.error.main }}>Age is less than min</span>
                  </Grid>
                </Grid>
              )}

              {!unvDate && !isAnyChildAgeLessThanMin && isAnyChildAgeGreaterThanMax && (
                <Grid container alignItems="flex-start">
                  <Grid item>
                    <IoFlagOutline color={theme.palette.error.main} style={{ marginTop: 4, marginRight: 10 }} />
                  </Grid>
                  <Grid item xs>
                    <span style={{ color: theme.palette.error.main }}>Age is greater than max</span>
                  </Grid>
                </Grid>
              )}
              <Grid container direction="row" justifyContent="space-between">
                <ButtonPrimary
                  style={{
                    backgroundColor: getStyleColor("--custom-color-rocketeer"),
                    color: "black",
                    fontWeight: "bold",
                  }}
                  disabled={!isBooked && (unvDate !== undefined || !isAllowedToBook || isAnyChildAgeLessThanMin)}
                  onClick={isBooked ? onRemove : onBook}
                >
                  {isBooked && "Remove Booking"}
                  {!isBooked && "Add to booking"}
                </ButtonPrimary>

                <Button onClick={onDismiss} variant="text">
                  Back
                </Button>
              </Grid>
            </Grid>
            {/* <Grid item>
              <Checkbox
                disabled={unvDate !== undefined}
                checked={props.program.isSelected}
                icon={
                  unvDate !== undefined ? (
                    <IoCloseCircleOutline size={32} />
                  ) : (
                    <IoEllipseOutline size={32} color={theme.palette.secondary.main} />
                  )
                }
                checkedIcon={<IoCheckmarkCircle size={32} />}
                onChange={(_ev, chk) => (props.onSelectedDateChanged ? props.onSelectedDateChanged(program, chk) : {})}
              />
            </Grid> */}
          </Grid>
        }
      />
    </ListItem>
  );
}

export default NationalProgramSelection;
